import { cn } from '@vidahealth/vida-ui';
import {
  type ReactNode,
  forwardRef,
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { Platform, ScrollView, StyleProp, View, ViewStyle } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { FrostedSurface } from '#modules/ui/FrostedSurface';
import { LoadingOverlay } from '#modules/ui/LoadingOverlay';

import { ScreenLayoutContext } from './hooks/useScreenLayout';
import { useScreenTopInset } from './hooks/useScreenTopInset';

type ScreenProps = {
  /**
   * Optional custom header content rendered as a sticky FrostedSurface
   * overlay above the scroll content. Use for one-off screens that own
   * their own top bar; flow-level chrome (e.g. the onboarding header) is
   * better placed in the route layout and propagated via
   * ScreenTopInsetProvider.
   */
  header?: ReactNode;
  footer?: ReactNode;
  children?: ReactNode;
  className?: string;
  contentStyle?: StyleProp<ViewStyle>;
  /**
   * Renders a translucent LoadingOverlay over the whole screen (content +
   * footer) and blocks touches — for an in-flight action like a slow save.
   * Saves callers wrapping `<Screen>` in their own positioned `View` + overlay.
   */
  busy?: boolean;
};

/** Imperative handle exposed via ref — e.g. to scroll back to the top when a
 * validation error needs to be surfaced. */
export type ScreenHandle = {
  scrollToTop: () => void;
  /**
   * Scrolls the content to an absolute Y offset within the ScrollView — e.g. a
   * grouped multi-question screen needs to bring a SPECIFIC invalid field into
   * view, not just the top. `animated` defaults to `true`, matching
   * `scrollToTop`.
   */
  scrollToOffset: (y: number, animated?: boolean) => void;
};

const MAX_CONTENT_WIDTH = 600;
const MIN_CONTENT_WIDTH = 320;

// Visual gap between the last content item and either the sticky footer or
// the device's bottom safe area. Used by both the ScrollView's padding-bottom
// and the layout-context value so descendants can size against the same edge.
const SCREEN_CONTENT_BOTTOM_GAP = 16;
const SCREEN_CONTENT_TOP_GAP = 24;

export const Screen = forwardRef<ScreenHandle, ScreenProps>(function Screen(
  { header, footer, children, className, contentStyle, busy },
  ref
) {
  const insets = useSafeAreaInsets();
  const { topInset: outerTopInset } = useScreenTopInset();
  const [headerHeight, setHeaderHeight] = useState(0);
  const [footerHeight, setFooterHeight] = useState(0);
  const scrollRef = useRef<ScrollView>(null);

  const scrollToTop = useCallback(() => {
    scrollRef.current?.scrollTo({ y: 0, animated: true });
  }, []);

  const scrollToOffset = useCallback((y: number, animated = true) => {
    scrollRef.current?.scrollTo({ y, animated });
  }, []);

  useImperativeHandle(ref, () => ({ scrollToTop, scrollToOffset }), [scrollToTop, scrollToOffset]);

  // Pick the top space the ScrollView needs to reserve so its content doesn't
  // hide under any sticky chrome. Priority: an outer layout's published
  // top inset (e.g. the onboarding header) → this Screen's own header
  // overlay → bare safe-area inset.
  const reservedTop = outerTopInset || (header ? headerHeight : insets.top);
  const paddingTop = reservedTop + SCREEN_CONTENT_TOP_GAP;
  const paddingBottom = (footer ? footerHeight : insets.bottom) + SCREEN_CONTENT_BOTTOM_GAP;

  return (
    <ScreenLayoutContext.Provider
      value={{ headerHeight, footerHeight, scrollContentPaddingBottom: paddingBottom }}
    >
      <View
        className={cn('flex-1 bg-background-0', className)}
        style={Platform.OS === 'web' ? { overflow: 'hidden' } : undefined}
      >
        <ScrollView
          ref={scrollRef}
          style={Platform.OS === 'web' ? { flex: 1 } : undefined}
          contentContainerStyle={{ flexGrow: 1, paddingTop, paddingBottom }}
          keyboardShouldPersistTaps='handled'
        >
          <View
            className='mx-auto w-full flex-1 px-6'
            style={[
              {
                maxWidth: MAX_CONTENT_WIDTH,
                minWidth: MIN_CONTENT_WIDTH,
              },
              contentStyle,
            ]}
          >
            {children}
          </View>
        </ScrollView>

        {header ? (
          <FrostedSurface
            // onLayout fires on mount and on size changes (rotation, web
            // viewport resize) — sufficient on both platforms now that we
            // measure the FrostedSurface itself rather than poking through
            // expo-blur's ref.
            onLayout={(e) => setHeaderHeight(e.nativeEvent.layout.height)}
            className='absolute left-0 right-0 top-0'
          >
            <View style={{ paddingTop: insets.top }}>{header}</View>
          </FrostedSurface>
        ) : null}

        {footer ? (
          // The frosted bar spans the full viewport width (edge to edge); its
          // inner column is capped to MAX_CONTENT_WIDTH and centered so the CTAs
          // line up with the body column. Capping the FrostedSurface itself
          // would leave the screen's left/right edges unfrosted on wide
          // viewports.
          //
          // Single source of bottom padding: the inner column's `paddingBottom`
          // is the device safe-area inset on native (iOS home indicator, Android
          // nav bar), falling back to 24-px breathing room on web / platforms
          // with no inset. The column owns top + sides too so the layers don't
          // compound.
          <FrostedSurface
            onLayout={(e) => setFooterHeight(e.nativeEvent.layout.height)}
            className='absolute bottom-0 left-0 right-0'
          >
            <View
              className='mx-auto w-full px-6 pt-6'
              style={{
                maxWidth: MAX_CONTENT_WIDTH,
                minWidth: MIN_CONTENT_WIDTH,
                paddingBottom: insets.bottom > 0 ? insets.bottom : 24,
              }}
            >
              {footer}
            </View>
          </FrostedSurface>
        ) : null}

        {/* Last child so it overlays the content and the sticky footer. */}
        {busy ? <LoadingOverlay /> : null}
      </View>
    </ScreenLayoutContext.Provider>
  );
});
